import {
  ChangeEvent,
  InputHTMLAttributes,
  SyntheticEvent,
  useMemo,
} from "react";
import { withMask } from "use-mask-input";
import { clsxm } from "@/utils/clsxm";

interface IProps {
  name: string;
  label: string;
  value: string;
  placeholder?: string;
  type?: "text" | "phone" | "dob";
  error?: string | boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void;
}

// Text input matching the SSN2 design-system Input and the SSN box outline:
// 1.5px border (#e7eae7 default, #4f41a6 focus, #cd0707 error), 52px tall,
// error text in the same red as the "couldn't verify" banner.
const VerifyTextInput = ({
  name,
  label,
  value,
  placeholder,
  type = "text",
  error,
  onChange,
  onBlur,
}: IProps) => {
  const maskProps: InputHTMLAttributes<HTMLInputElement> = useMemo(() => {
    if (type === "phone") {
      return {
        ref: withMask("(999) 999-9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "tel",
        pattern: "[0-9]*",
      };
    }
    if (type === "dob") {
      return {
        ref: withMask("99/99/9999", {
          jitMasking: true,
          showMaskOnHover: false,
        }),
        inputMode: "numeric",
        pattern: "[0-9]*",
      };
    }
    return {};
  }, [type]);

  return (
    <div className="flex w-full flex-col gap-1.5">
      <label className="text-sm font-medium text-[#303030]">{label}</label>
      <input
        {...maskProps}
        name={name}
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        type="text"
        placeholder={placeholder}
        className={clsxm(
          "h-[52px] w-full rounded-xl border-[1.5px] bg-white px-4 text-base text-charcoal outline-none transition-colors placeholder:text-cool-gray",
          error
            ? "border-[#cd0707]"
            : "border-[#e7eae7] focus:border-[#4f41a6]"
        )}
      />
      {error && typeof error === "string" && (
        <span className="text-[13px] leading-[1.5] text-[#cd0707]">{error}</span>
      )}
    </div>
  );
};

export { VerifyTextInput };
